import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { fetchCompare } from '@/lib/api';
import { getGradeColor, PILLAR_CONFIG } from '@/lib/constants';
import { X, GitCompareArrows } from 'lucide-react';

export default function CompareModal({ open, onClose, compareIds, onRemove }) {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && compareIds.length >= 2) {
      setLoading(true);
      fetchCompare(compareIds)
        .then(data => setCompanies(data.companies || []))
        .catch(console.error)
        .finally(() => setLoading(false));
    }
  }, [open, compareIds]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0D2238] border-white/15 text-white max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="company-compare-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitCompareArrows className="w-5 h-5 text-[#2A9D8F]" />
            Compare Companies
          </DialogTitle>
        </DialogHeader>

        {compareIds.length < 2 ? (
          <div className="text-center py-8">
            <p className="text-white/50">Select at least 2 companies to compare</p>
            <p className="text-white/30 text-sm mt-2">Use the compare toggle on company cards</p>
          </div>
        ) : loading ? (
          <div className="text-center py-8">
            <p className="text-white/50">Loading comparison...</p>
          </div>
        ) : (
          <div>
            {/* Company headers */}
            <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${companies.length}, 1fr)` }}>
              {companies.map(c => {
                const gradeColor = getGradeColor(c.grade);
                return (
                  <div key={c.id} className="rounded-xl bg-white/5 border border-white/10 p-4 text-center relative">
                    <button
                      onClick={() => onRemove(c.id)}
                      className="absolute top-2 right-2 p-1 rounded-full hover:bg-white/10 text-white/30 hover:text-white/60"
                    >
                      <X className="w-3 h-3" />
                    </button>
                    <h4 className="text-sm font-bold text-white">{c.name}</h4>
                    <p className="text-[10px] text-white/40 mt-0.5">{c.sector} &middot; {c.sub_sector}</p>
                    <p className="font-mono text-3xl font-bold tabular-nums mt-2" style={{ color: gradeColor }}>
                      {c.overall_score}
                    </p>
                    <Badge className="mt-1 text-xs font-bold" style={{ backgroundColor: `${gradeColor}2E`, color: gradeColor }}>
                      {c.grade}
                    </Badge>
                  </div>
                );
              })}
            </div>

            {/* Pillar Comparison */}
            <div className="mt-6">
              <h3 className="text-sm font-semibold text-white/60 mb-3 uppercase tracking-wider">Pillar Comparison</h3>
              <div className="space-y-4">
                {PILLAR_CONFIG.map(p => (
                  <div key={p.key}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-white/70">{p.name}</span>
                      <span className="text-[10px] text-white/30">{p.weight}% weight</span>
                    </div>
                    <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${companies.length}, 1fr)` }}>
                      {companies.map(c => (
                        <div key={c.id}>
                          <div className="h-3 bg-white/5 rounded-full overflow-hidden">
                            <div
                              className="h-full rounded-full"
                              style={{ width: `${c[p.key]}%`, backgroundColor: p.color }}
                            />
                          </div>
                          <p className="font-mono text-xs text-center mt-1 tabular-nums" style={{ color: p.color }}>{c[p.key]}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Key Strengths Comparison */}
            <div className="mt-6">
              <h3 className="text-sm font-semibold text-white/60 mb-3 uppercase tracking-wider">Key Strengths</h3>
              <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${companies.length}, 1fr)` }}>
                {companies.map(c => (
                  <div key={c.id} className="space-y-1">
                    {c.key_strengths && c.key_strengths.map((s, i) => (
                      <p key={i} className="text-[11px] text-white/50">+ {s}</p>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
